import { useEffect, useState } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { Mail, ShieldCheck, Loader2 } from 'lucide-react';

/**
 * Email notification preferences panel — lives on /app/profile.
 *
 * Backend supplies the category catalog via GET /api/me/notifications so we
 * can add or reword categories on the server without shipping a frontend
 * change. Security-critical categories arrive with `always_on: true` and are
 * rendered as locked toggles with an explainer.
 */
export default function EmailPreferences() {
  const [state, setState] = useState({ loading: true, categories: [], prefs: {} });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get('/me/notifications');
        if (!cancelled) setState({ loading: false, categories: r.data?.categories || [], prefs: r.data?.prefs || {} });
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
        toast.error('Could not load your notification settings.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async (key, alwaysOn) => {
    if (alwaysOn) return;
    const next = { ...state.prefs, [key]: !state.prefs[key] };
    setState((s) => ({ ...s, prefs: next }));
    setSaving(true);
    try {
      const r = await http.patch('/me/notifications', {
        prefs: Object.fromEntries(
          state.categories.filter((c) => !c.always_on).map((c) => [c.key, next[c.key] ?? true])
        ),
      });
      setState((s) => ({ ...s, prefs: r.data?.prefs || next }));
      toast.success(next[key] ? 'On — you\'ll receive these.' : 'Off — you won\'t receive these.');
    } catch {
      toast.error('Could not save. Try again.');
      // Rollback on error
      setState((s) => ({ ...s, prefs: { ...s.prefs, [key]: !next[key] } }));
    } finally { setSaving(false); }
  };

  if (state.loading) {
    return (
      <div className="bg-white rounded-3xl p-6 card-soft flex items-center gap-3 text-navy/60" data-testid="email-prefs-loading">
        <Loader2 size={16} className="animate-spin" /> Loading notification settings…
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl card-soft overflow-hidden" data-testid="email-prefs-panel">
      <div className="p-6 border-b border-navy/8 flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-gold/15 text-gold flex items-center justify-center">
          <Mail size={18} strokeWidth={1.75}/>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest text-navy/50 font-semibold">Email preferences</p>
          <h3 className="font-heading text-lg text-navy">What we email you about</h3>
        </div>
      </div>
      <div className="divide-y divide-navy/8">
        {state.categories.map((c) => {
          const on = c.always_on ? true : !!state.prefs[c.key];
          return (
            <div key={c.key} className="p-5 flex items-start gap-4" data-testid={`email-pref-${c.key}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-navy">{c.label}</p>
                  {c.always_on && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-gold font-semibold">
                      <ShieldCheck size={11}/> always on
                    </span>
                  )}
                </div>
                <p className="text-xs text-navy/60 mt-1 leading-relaxed">{c.description}</p>
                {c.always_on && (
                  <p className="text-[11px] text-navy/45 mt-1.5 italic">
                    Required for account safety — can&apos;t be turned off.
                  </p>
                )}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label={`${c.label} — ${on ? 'on' : 'off'}`}
                disabled={c.always_on || saving}
                onClick={() => toggle(c.key, c.always_on)}
                data-testid={`email-pref-toggle-${c.key}`}
                className={`relative w-12 h-7 rounded-full transition-smooth shrink-0
                  ${on ? 'bg-navy' : 'bg-navy/15'}
                  ${c.always_on ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:brightness-110'}`}
              >
                <span
                  className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform
                    ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
